/**
 * Controller input: the user's bindings (persisted per user, like `key_bindings`) and the MIDI runtime state (not persisted).
 *
 * The controller travels with the operator between projects, so bindings are not project data. See context/input.md.
 */
import { createSlice } from '@reduxjs/toolkit'
import { makeBinding, sameSource } from '~/lib/input/model'

const MAX_UNDO = 50

const initialState = {
	bindings: [],
	// The user turned MIDI on and the browser granted it: it starts at load (`resumeMidi`). A refusal turns it off.
	midiOn: false,
	// `deviceKey`s of the inputs the user turned off: their messages are ignored. Mappings stay device-agnostic.
	disabledInputs: [],
	midi: {
		// 'off' | 'pending' | 'ready' | 'unsupported' | 'denied'
		status: 'off',
		// Connected inputs, as `describePorts` gives them.
		inputs: [],
	},
	// Learn mode (runtime). `target` is the selected target string; `add` is Alt held at its click; `slot` is the clicked cell's place in a range target.
	learn: {
		on: false,
		target: null,
		add: false,
		slot: null,
		// `{ target, from: [targets] }`: what its last bind took a control or key off.
		taken: null,
	},
	/**
	 * Learn's own undo: snapshots `{ bindings, keys }` (keys: `key_bindings.bindings`, which keyboard learn writes). ⚠️ The app history snapshots `live` only, so bindings cannot ride it — the surfaces' own stack is the precedent. Runtime, not persisted.
	 */
	undo: { past: [], future: [] },
}

// The same array when nothing names `targets`, so an unbind of an unbound target is no edit (no undo step).
const dropTargets = (bindings, targets) =>
	bindings.some(b => b.targets.some(t => targets.has(t.target)))
		? bindings
				.map(b => ({ ...b, targets: b.targets.filter(t => !targets.has(t.target)) }))
				.filter(b => b.targets.length)
		: bindings

const inputSlice = createSlice({
	name: 'input',
	initialState,
	reducers: {
		/** `{ binding, add? }`: another binding on the same control is replaced unless `add` (learn's Alt). */
		addBinding: {
			reducer: (s, { payload: { binding, add } }) => {
				if (!add) s.bindings = s.bindings.filter(x => !sameSource(x, binding))
				s.bindings.push(binding)
			},
			// The id is minted here, so the reducer stays pure.
			prepare: ({ binding, add = false }) => ({ payload: { binding: makeBinding(binding), add } }),
		},
		/**
		 * Learn's bind, as one undo step: `{ binding, add }`. Without `add` the control MOVES — any binding on it goes — and the target is re-bound rather than given a second control; with `add` (Alt) both stay.
		 */
		learnBinding: {
			reducer: (s, { payload: { binding, add } }) => {
				if (!add) {
					s.bindings = dropTargets(
						s.bindings.filter(x => !sameSource(x, binding)),
						new Set(binding.targets.map(t => t.target))
					)
				}
				s.bindings.push(binding)
			},
			prepare: ({ binding, add = false }) => ({ payload: { binding: makeBinding(binding), add } }),
		},
		/** The whole list, as undo restores it; each entry re-runs through `makeBinding`. */
		setBindings: (s, { payload }) => {
			s.bindings = (payload ?? []).map(makeBinding)
		},
		/** `{ id, patch }`; `patch.targets` replaces the list. */
		updateBinding: (s, { payload: { id, patch } }) => {
			const i = s.bindings.findIndex(b => b.id === id)
			if (i >= 0) s.bindings[i] = makeBinding({ ...s.bindings[i], ...patch, id })
		},
		removeBinding: (s, { payload: id }) => {
			s.bindings = s.bindings.filter(b => b.id !== id)
		},
		/** Unbind a target everywhere: its entries go, and a binding left with no target goes with them. */
		removeTargetBindings: (s, { payload: target }) => {
			s.bindings = dropTargets(s.bindings, new Set([target]))
		},
		setMidiOn: (s, { payload }) => {
			s.midiOn = !!payload
		},
		/** `{ key, enabled }`: a device's `deviceKey`. */
		setInputEnabled: (s, { payload: { key, enabled } }) => {
			s.disabledInputs = s.disabledInputs.filter(k => k !== key)
			if (!enabled) s.disabledInputs.push(key)
		},
		setLearn: (s, { payload: on }) => {
			s.learn = { on: !!on, target: null, add: false, slot: null, taken: null }
		},
		/** `{ target, add, slot }`; a null target deselects. */
		setLearnTarget: (s, { payload: { target, add = false, slot = null } }) => {
			s.learn.target = target ?? null
			s.learn.add = !!(target && add)
			s.learn.slot = target ? slot : null
			s.learn.taken = null
		},
		setLearnTaken: (s, { payload }) => {
			s.learn.taken = payload
		},
		/** Record the state before an edit; a new edit clears redo. */
		pushLearnUndo: (s, { payload }) => {
			s.undo.past.push(payload)
			if (s.undo.past.length > MAX_UNDO) s.undo.past.shift()
			s.undo.future = []
		},
		/** One step across the stacks: undo pops `past` and files `current` under `future`, redo the reverse. The thunk restores the snapshot it read before. */
		stepLearnUndo: (s, { payload: { redo, current } }) => {
			s.learn.taken = null
			const { past, future } = s.undo
			if (redo) {
				if (!future.length) return
				future.shift()
				past.push(current)
			} else {
				if (!past.length) return
				past.pop()
				future.unshift(current)
			}
		},
		setMidiStatus: (s, { payload }) => {
			s.midi.status = payload
		},
		setMidiInputs: (s, { payload }) => {
			s.midi.inputs = payload
		},
	},
})

export const {
	addBinding,
	learnBinding,
	setBindings,
	setLearn,
	setLearnTarget,
	setLearnTaken,
	pushLearnUndo,
	stepLearnUndo,
	updateBinding,
	removeBinding,
	removeTargetBindings,
	setMidiOn,
	setInputEnabled,
	setMidiStatus,
	setMidiInputs,
} = inputSlice.actions

export default inputSlice.reducer
